import { Reward, RewardsMap } from 'features/reward/types';

import { getParentIdFromUrl } from 'utils/cart/get-parent-id-from-url';
import { CartEntryType } from 'utils/cart/types';

export type RewardBenefitType = 'item' | 'combo' | 'picker';
export type RewardSelectionType = 'item' | 'combo';

export interface RewardBenefit {
  id: string;
  type?: RewardBenefitType;
}

export interface RewardSelectionCartEntry {
  sanityId?: string;
  _id?: string;
  type?: string;
  name?: string | null;
  url?: string;
  children?: RewardSelectionCartEntry[];
}

export interface RewardSelection {
  id: string;
  type: RewardSelectionType;
  url: string;
}

const isLiveBenefitType = (type?: RewardBenefitType): type is RewardSelectionType =>
  type === 'item' || type === 'combo';

const getIncentiveType = (reward: Reward): RewardBenefitType | undefined => {
  if (!Array.isArray(reward.incentives)) {
    return undefined;
  }

  const incentive = reward.incentives[0];
  if (!incentive || typeof incentive !== 'object') {
    return undefined;
  }

  const type = (incentive as { _type?: unknown })._type;
  if (type === 'item' || type === 'combo' || type === 'picker') {
    return type;
  }

  return undefined;
};

const getCartEntryId = (cartEntry: RewardSelectionCartEntry): string | undefined =>
  cartEntry.sanityId || cartEntry._id || undefined;

const getCartEntryBenefitId = (cartEntry: RewardSelectionCartEntry): string | undefined => {
  const cartEntryId = getCartEntryId(cartEntry);
  if (!cartEntryId || cartEntry.type !== CartEntryType.combo) {
    return cartEntryId;
  }

  const [, benefitId] = cartEntryId.split('$');
  return benefitId || cartEntryId;
};

const toSelectionType = (type?: string): RewardSelectionType | undefined => {
  if (type === CartEntryType.item || type === 'item') {
    return 'item';
  }

  if (type === CartEntryType.combo || type === 'combo') {
    return 'combo';
  }

  return undefined;
};

const extractRewardSelectionCartEntry = (
  cartEntry: RewardSelectionCartEntry
): RewardSelectionCartEntry | undefined => {
  const children = cartEntry.children;
  const hasValidChildren = Array.isArray(children) && children.length > 0;

  if (cartEntry.type === CartEntryType.comboSlot && hasValidChildren) {
    const foundEntry = children.find(
      entry => entry.name && entry.name.toLowerCase() !== 'combo sides'
    );

    if (foundEntry) {
      return foundEntry;
    }
  }

  if (hasValidChildren) {
    for (const entry of children) {
      const extracted = extractRewardSelectionCartEntry(entry);
      if (extracted) {
        return extracted;
      }
    }
  }

  return undefined;
};

const toRewardSelection = (id: string, type: RewardSelectionType): RewardSelection => ({
  id,
  type,
  url: `/menu/${type}-${id}`,
});

/** Resolves a Reward by engine id, then Reward document id (`cmsId`). Never by benefit id. */
export function getReward(rewardsMap: RewardsMap, key: string): Reward | undefined {
  return rewardsMap[key] ?? Object.values(rewardsMap).find(reward => reward.cmsId === key);
}

/** Granted menu object: item, combo, or picker. */
export function getRewardBenefit(reward: Reward): RewardBenefit | undefined {
  if (!reward.rewardBenefitId) {
    return undefined;
  }

  return {
    id: reward.rewardBenefitId,
    type: getIncentiveType(reward),
  };
}

/** Finds the first Reward whose granted benefit matches a cart menu line. */
export function findMatchingRewardForCartEntry(
  rewards: ReadonlyArray<Reward>,
  cartEntry: RewardSelectionCartEntry
): Reward | undefined {
  const cartEntryId = getCartEntryBenefitId(cartEntry);
  const parentId = getParentIdFromUrl(cartEntry.url);
  if (!cartEntryId && !parentId) {
    return undefined;
  }

  return rewards.find(reward => {
    const benefit = getRewardBenefit(reward);
    return benefit?.id === cartEntryId || benefit?.id === parentId;
  });
}

/**
 * Chosen item or combo. Item/combo benefits with a known incentive type use the live benefit id
 * after extract. Picker or unknown types keep the extracted child. Extract failure is no selection.
 */
export function getRewardSelection(
  reward: Reward,
  cartEntry: RewardSelectionCartEntry
): RewardSelection | undefined {
  const extracted = extractRewardSelectionCartEntry(cartEntry);
  if (!extracted) {
    return undefined;
  }

  const benefit = getRewardBenefit(reward);
  if (benefit && isLiveBenefitType(benefit.type)) {
    return toRewardSelection(benefit.id, benefit.type);
  }

  const extractedId = getCartEntryId(extracted);
  const extractedType = toSelectionType(extracted.type);
  if (!extractedId || !extractedType) {
    return undefined;
  }

  return toRewardSelection(extractedId, extractedType);
}
